import { Grid, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { ClientType } from '@/types';
import {
  usePaymentMethod,
  useInvoiceCurrency,
  useInvoicingCompanyId,
  useRecipientType,
  useSelectedServices,
  useIndividualDetails,
  useManualInvoiceActions,
} from '@/models/manual-invoice.store';
import { useInvoicingCompanies } from '@/models/renewals';
import {
  useInvoiceCalculation,
  getVatExplanation,
} from './hooks/useInvoiceCalculation';
import IssuerSection from './sections/IssuerSection';
import InvoiceMetaSection from './sections/InvoiceMetaSection';
import RecipientSection from './sections/RecipientSection';
import ServicesSection from './sections/ServicesSection';
import TotalsSection from './sections/TotalsSection';
import styles from './InvoiceEditor.module.css';

export default function InvoiceEditor() {
  const paymentMethod = usePaymentMethod();
  const invoiceCurrency = useInvoiceCurrency();
  const invoicingCompanyId = useInvoicingCompanyId();
  const recipientType = useRecipientType();
  const services = useSelectedServices();
  const individualDetails = useIndividualDetails();
  const { setInvoicingCompany } = useManualInvoiceActions();

  const { data: companies = [], isLoading: companiesLoading } =
    useInvoicingCompanies();

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === invoicingCompanyId) || null,
    [companies, invoicingCompanyId],
  );

  // Get recipient country for VAT calculation
  const recipientCountry = useMemo(() => {
    if (recipientType === ClientType.Individual) {
      return individualDetails.residenceCountry;
    }
    return '';
  }, [recipientType, individualDetails.residenceCountry]);

  // Calculate invoice totals
  const totals = useInvoiceCalculation({
    services,
    invoiceCurrency,
    paymentMethod,
    invoicingCompany: selectedCompany,
    recipientCountry,
  });

  // Get VAT explanation
  const vatExplanation = useMemo(
    () => getVatExplanation(selectedCompany, recipientCountry),
    [selectedCompany, recipientCountry],
  );

  return (
    <div className={styles.editor}>
      <Grid gap="md">
        {/* Left column: Issuer + Invoice Meta */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap="md">
            <IssuerSection
              companies={companies}
              selectedCompanyId={invoicingCompanyId}
              onCompanyChange={setInvoicingCompany}
              isLoading={companiesLoading}
            />
          </Stack>
        </Grid.Col>

        {/* Right column: Invoice Details */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <InvoiceMetaSection />
        </Grid.Col>

        {/* Full width: Recipient */}
        <Grid.Col span={12}>
          <RecipientSection />
        </Grid.Col>

        {/* Full width: Services */}
        <Grid.Col span={12}>
          <ServicesSection />
        </Grid.Col>

        {/* Full width: Totals */}
        <Grid.Col span={12}>
          <TotalsSection
            totals={totals}
            invoiceCurrency={invoiceCurrency}
            paymentMethod={paymentMethod}
            vatExplanation={vatExplanation}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
}
